import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { StarBurst } from "../ui/StarBurst";
import type { WordMatchQuestion } from "../levelTypes";

interface Props {
  question: WordMatchQuestion;
  allQuestions: WordMatchQuestion[];
  onAnswer: (result: "correct" | "wrong") => void;
}

type Phase = "idle" | "correct" | "wrong";

const LETTERS = ["A", "B", "C", "D"];

/** Pick 3 wrong distractors from the pool, shuffled with the correct word. */
function buildOptions(correct: WordMatchQuestion, all: WordMatchQuestion[]): WordMatchQuestion[] {
  const pool = all.filter((q) => q.word !== correct.word);
  const shuffledPool = [...pool].sort(() => Math.random() - 0.5);
  const distractors = shuffledPool.slice(0, 3);
  const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
  return options;
}

export function WordMatch({ question, allQuestions, onAnswer }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [chosenWord, setChosenWord] = useState<string | null>(null);

  // Regenerate options only when question changes
  const options = useMemo(
    () => buildOptions(question, allQuestions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [question.word]
  );

  const [imgError, setImgError] = useState(false);

  function handleChoice(word: string) {
    if (phase !== "idle") return;
    const isCorrect = word === question.word;
    setChosenWord(word);
    setPhase(isCorrect ? "correct" : "wrong");

    setTimeout(() => {
      onAnswer(isCorrect ? "correct" : "wrong");
    }, 1500);
  }

  return (
    <div className="exercise-card">
      <StarBurst active={phase === "correct"} />

      {/* Image */}
      <div className="wordmatch__image-wrap">
        {question.imageUrl && !imgError ? (
          <img
            src={question.imageUrl}
            alt={question.translation}
            className="wordmatch__image"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="wordmatch__image-placeholder">🖼️</span>
        )}
      </div>

      {/* Prompt */}
      <p className="wordmatch__prompt">
        Hva er <strong>{question.translation}</strong> på samisk?
      </p>

      {/* Options */}
      <motion.div
        className="wordmatch__options"
        animate={
          phase === "wrong"
            ? { x: [0, -10, 10, -8, 8, -4, 4, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.4 }}
      >
        {options.map((opt, i) => {
          const isChosen = chosenWord === opt.word;
          const isCorrectOpt = opt.word === question.word;

          let extraClass = "";
          if (phase !== "idle") {
            if (isCorrectOpt) extraClass = " option-btn--correct";
            else if (isChosen) extraClass = " option-btn--wrong";
          }

          return (
            <motion.button
              key={opt.word}
              className={`option-btn${extraClass}`}
              data-opt={i}
              disabled={phase !== "idle"}
              onClick={() => handleChoice(opt.word)}
              whileTap={phase === "idle" ? { scale: 0.97 } : {}}
            >
              <span className="option-btn__letter">{LETTERS[i]}</span>
              {opt.word}
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}
